import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import PropTypes from 'prop-types';
import Popover from '../../../components/Popover/Popover';
import Button from '../../../components/Button/Button';
import CountryList from '../../../components/CountryList/CountryList';
import { t } from '../../../i18n';
import s from '../WatchtowerPage.module.scss';
import { useMap } from '../../../components/Map/MapContext';
import classNames from 'classnames';
import Flag from '../../../components/Flag/Flag';
import Icon from '../../../components/Icon';

const CountryLayer = ({ isCheckbox = false, isOpen, onToggle }) => {
    const { countryList } = useMap();
    const { countries, activeCountryCode, onCountryClick } = countryList;
    const popoverRef = useRef(null);
    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const searchParams = useSearchParams();

    const toggleFilterDropdown = () => {
        setIsFilterDropdownOpen(!isFilterDropdownOpen);
    };

    const handleCountryClick = (country) => {
        onCountryClick(country);
        setIsFilterDropdownOpen(false);

        const params = new URLSearchParams(searchParams.toString());
        if (country) {
            params.set('activeCountry', country.code);
            sessionStorage.setItem('activeCountry', country.code);
        } else {
            params.delete('activeCountry');
            sessionStorage.removeItem('activeCountry');
        }

        const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
        window.history.replaceState({}, '', newUrl);
    };

    useEffect(() => {
        const activeCountryFromUrl = searchParams.get('activeCountry');
        const activeCountryFromStorage =
            sessionStorage.getItem('activeCountry');

        let country = null;

        if (activeCountryFromUrl) {
            sessionStorage.setItem('activeCountry', activeCountryFromUrl);

            country = countries.find((c) => c.code === activeCountryFromUrl);
        } else if (activeCountryFromStorage) {
            const params = new URLSearchParams(searchParams.toString());
            params.set('activeCountry', activeCountryFromStorage);
            const newUrl = `${window.location.pathname}${params.toString() ? `?${params.toString()}` : ''}`;
            window.history.replaceState({}, '', newUrl);

            country = countries.find(
                (c) => c.code === activeCountryFromStorage
            );
        }

        if (country) {
            setTimeout(() => {
                onCountryClick(country);
            }, 250);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target)
            ) {
                setIsFilterDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [popoverRef]);

    return (
        <div className={s.LayerPicker} ref={popoverRef}>
            <Popover
                isPopoverOpen={isFilterDropdownOpen}
                minWidth="233px"
                modifier=""
                onClick={toggleFilterDropdown}
                content={
                    <CountryList
                        countries={countries}
                        activeCountryCode={activeCountryCode}
                        onCountryClick={handleCountryClick}
                        isCheckbox={isCheckbox}
                        isOpen={isOpen}
                        onToggle={onToggle}
                        isFlowmap
                        disableMobileStyle={true}
                    />
                }>
                <div
                    className={classNames(s.CountrySelectorButton, {
                        [s.Open]: isFilterDropdownOpen,
                    })}>
                    <div className={s.CountrySelectorIconWrapper}>
                        {activeCountryCode ? (
                            <Flag
                                width="2.4rem"
                                countryCode={activeCountryCode}
                                className={s.CountrySelectorIcon}
                            />
                        ) : (
                            <Icon
                                name="Flag"
                                className={s.CountrySelectorIcon}
                            />
                        )}

                        <span className={s.CountrySelectorText}>
                            {activeCountryCode
                                ? countries.find(
                                      (c) => c.code === activeCountryCode
                                  )?.name
                                : t('general.selectCountry')}
                        </span>
                    </div>
                    <Icon
                        name="ArrowRightBlack"
                        className={s.CountrySelectorArrowIcon}
                    />
                </div>
            </Popover>
        </div>
    );
};

CountryLayer.propTypes = {
    countries: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    activeCountryCode: PropTypes.string,
    onCountryClick: PropTypes.func.isRequired,
    isCheckbox: PropTypes.bool,
};

export default CountryLayer;
